// KaratChart.jsx - Every marking a person is likely to find stamped on a piece,
// what it means, and which ones mean plated rather than solid.
//
// This is the highest intent reference page on the site. Somebody squinting at
// "585" inside a ring is about thirty seconds away from wondering what it is
// worth, so the calculators and the kit are one click from every row.

import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, faqSchema, SITE } from "../lib/seo.jsx";
import { useSpot } from "../lib/hooks.js";
import { moneyExact } from "../lib/format.js";
import { Panel, Tag } from "../ui/kit.jsx";
import { ArrowRight } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { HallmarkZoom, Loupe } from "../ui/illustrations.jsx";
import { PageHead, CtaBand, FaqList, MobileCta, EmbedThis } from "./parts.jsx";

////////// MARKINGS //////////
// `solid` false means the mark indicates a coating, not an alloy. Those rows are
// the ones people most need explained, and the ones least often explained fairly.

const GOLD = [
  { mark: "24K, 999, 9999", meaning: "Pure gold", factor: 0.999, solid: true },
  { mark: "22K, 916, 917", meaning: "22 parts gold in 24", factor: 0.916, solid: true },
  { mark: "18K, 750", meaning: "18 parts gold in 24", factor: 0.75, solid: true },
  { mark: "14K, 585, 583", meaning: "14 parts gold in 24", factor: 0.583, solid: true },
  { mark: "10K, 417", meaning: "10 parts gold in 24, the US legal minimum to be called gold", factor: 0.417, solid: true },
  { mark: "9K, 375", meaning: "9 parts gold in 24, common on British pieces", factor: 0.375, solid: true },
  { mark: "GF, 1/20 12K GF", meaning: "Gold filled. A bonded layer, typically 5 percent of the weight", factor: 0, solid: false },
  { mark: "RGP, HGE, GEP", meaning: "Rolled gold plate or electroplate. A microscopic layer", factor: 0, solid: false },
  { mark: "GP, GT, EP", meaning: "Gold plated or gold tone. No recoverable value", factor: 0, solid: false },
];

const SILVER = [
  { mark: "999, FINE", meaning: "Fine silver, usually bullion", factor: 0.999, solid: true },
  { mark: "STERLING, 925, STER", meaning: "Sterling silver", factor: 0.925, solid: true },
  { mark: "900, COIN", meaning: "Coin silver, and pre-1965 US dimes, quarters and halves", factor: 0.9, solid: true },
  { mark: "800, 830, 835", meaning: "Continental European silver", factor: 0.8, solid: true },
  { mark: "EPNS, EP, A1, IS", meaning: "Electroplated nickel silver. Plated, no melt value", factor: 0, solid: false },
  { mark: "GERMAN SILVER, NICKEL SILVER, ALPACA", meaning: "Contains no silver at all despite the name", factor: 0, solid: false },
];

const PLATINUM = [
  { mark: "PLAT, PT950, 950", meaning: "95 percent platinum, the usual jewellery standard", factor: 0.95, solid: true },
  { mark: "PT900, 900PT", meaning: "90 percent platinum", factor: 0.9, solid: true },
  { mark: "IRIDPLAT, 10IRID90PLAT", meaning: "90 percent platinum with iridium", factor: 0.9, solid: true },
  { mark: "PD950, PALL", meaning: "Palladium, a different metal at a different price", factor: 0, solid: true },
];

const FAQS = [
  {
    question: "There is no mark on my piece at all. Is it worthless?",
    answer: "Not necessarily. Older pieces, especially European and handmade ones, were often never stamped, and marks wear off clasps and ring shanks over decades. An unmarked piece has to be tested rather than read, which is exactly what we do with every item that arrives.",
  },
  {
    question: "What does 1/20 12K GF mean?",
    answer: "Gold filled. One twentieth of the total weight is a bonded layer of 12K gold. On a 20 gram bracelet that is one gram of 12K, which contains about half a gram of pure gold. It is real but it is rarely worth the cost of recovering.",
  },
  {
    question: "My ring says 925 but it looks like gold.",
    answer: "It is sterling silver with a gold coloured plating, usually called vermeil if the plating is real gold. It is valued as silver.",
  },
  {
    question: "Can a mark be fake?",
    answer: "Yes, and it happens more than people expect on chains bought abroad or online. It is one reason we test every piece rather than trusting the stamp, and why the test method appears next to every line on your offer.",
  },
];

function Table({ title, rows, metal, spot }) {
  const perGram = spot?.perGram?.[metal];

  return (
    <Reveal as={Panel} className="overflow-hidden">
      <p className="eyebrow px-5 py-3.5 border-b border-rule">{title}</p>
      <div className="sm:overflow-x-auto">
        <table className="stack-table w-full sm:min-w-[520px] text-[14.5px]">
          <thead>
            <tr className="border-b border-rule-soft">
              <th className="text-left eyebrow font-semibold px-5 py-2.5">Marking</th>
              <th className="text-left eyebrow font-semibold px-3 py-2.5">What it means</th>
              <th className="text-right eyebrow font-semibold px-3 py-2.5">Pure</th>
              <th className="text-right eyebrow font-semibold px-5 py-2.5">Per gram</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.mark} className="border-b border-rule-soft last:border-0">
                <td data-label="" className="px-5 py-3 font-semibold text-ink sm:whitespace-nowrap num">{row.mark}</td>
                <td data-label="Means" className="px-3 py-3 text-muted">
                  <span>{row.meaning}</span>
                  {!row.solid && <Tag className="ml-2 align-middle">Plated</Tag>}
                </td>
                <td data-label="Pure" className="px-3 py-3 text-right num text-muted">{row.factor ? `${(row.factor * 100).toFixed(1)}%` : "—"}</td>
                <td data-label="Per gram" className="px-5 py-3 text-right num text-ink">
                  {row.factor && perGram ? moneyExact(perGram * row.factor) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Reveal>
  );
}

export default function KaratChart() {
  const { spot } = useSpot();

  return (
    <>
      <Seo
        title="Karat and hallmark chart"
        path="/tools/karat-chart"
        description="What 585, 750, 925, PLAT, GF and EPNS actually mean. A complete chart of gold, silver and platinum markings with the pure metal content of each."
        image={SITE.ogTools}
      />
      <JsonLd data={breadcrumbSchema([
        { label: "Home", to: "/" },
        { label: "Calculators", to: "/tools" },
        { label: "Karat and hallmark chart", to: "/tools/karat-chart" },
      ])} />
      <JsonLd data={faqSchema(FAQS)} />

      <PageHead
        eyebrow="Reference"
        title="What the stamp inside your ring means."
        lede="Every marking you are likely to find, what it means, and the ones that look like precious metal but are a coating. The last column is what one gram of that alloy is worth at today's spot price."
        art={() => <HallmarkZoom className="max-w-[19rem] ml-auto" />}
      />

      <div className="shell pb-14 md:pb-24 space-y-7">
        <Table title="Gold" rows={GOLD} metal="gold" spot={spot} />
        <Table title="Silver" rows={SILVER} metal="silver" spot={spot} />
        <Table title="Platinum and palladium" rows={PLATINUM} metal="platinum" spot={spot} />

        <StaggerBoxed as="div" step={90} className="grid gap-6 md:grid-cols-3 pt-4">
          {[
            ["Where to look", "Inside a ring band, on the flat face of a clasp, on the post of an earring, on the underside of a spoon handle, or on the tongue of a bracelet."],
            ["Bring a loupe or a phone", "Most marks are under a millimetre tall. A phone camera on maximum zoom, held very still, reads them better than most people's eyes."],
            ["A mark is a claim, not a fact", "We test every piece regardless of what it says, and the test method appears next to every line on your offer."],
          ].map(([title, body]) => (
            <div key={title}>
              <h2 className="display display-sm text-ink">{title}</h2>
              <p className="hint mt-2 leading-relaxed">{body}</p>
            </div>
          ))}
        </StaggerBoxed>

        <div className="pt-6 grid gap-8 lg:grid-cols-[0.65fr_1.35fr] lg:gap-14">
          <Reveal>
            <h2 className="display display-lg text-ink">Common questions</h2>
            <Loupe className="hidden lg:block mt-6 max-w-[13rem]" />
          </Reveal>
          <Reveal delay={110}>
            <FaqList items={FAQS} />
          </Reveal>
        </div>

        <div className="flex flex-col xs:flex-row flex-wrap gap-3 pt-4">
          <Link to="/tools/gold-calculator" className="btn btn-outline">Work out what yours is worth <ArrowRight size={16} /></Link>
          <Link to="/what-we-buy" className="btn btn-quiet">See what we buy</Link>
        </div>
      </div>

      <div className="shell pb-10">
        <EmbedThis tool="karat" metal="gold" />
      </div>

      <CtaBand />
      <MobileCta />
    </>
  );
}
